import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { debounceTime, filter, firstValueFrom } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { ComplianceApiService } from 'src/shared/services/compliance-api.service';
import { ComplianceItem } from 'src/models/compliance.model';

@Component({
  templateUrl: 'compliance-item-detail.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ComplianceItemDetailPage implements OnInit {
  private complianceApi = inject(ComplianceApiService);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(UntypedFormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  util = inject(Util);
  readonly windowService = inject(WindowService);

  form: UntypedFormGroup;
  loaded = false;
  isRightSideContent = true;
  dialogParams: any;
  item: ComplianceItem;
  errorMessage: string;
  isSaving = false;
  private initialLoadComplete = false;
  private pendingSave = false;

  bucketItems = [
    { viewValue: 'Blockers', value: 'blockers' },
    { viewValue: 'Before Listing', value: 'before_listing' },
    { viewValue: 'Operate', value: 'operate' },
    { viewValue: 'Taxes', value: 'taxes' },
    { viewValue: 'Admin', value: 'admin' },
    { viewValue: 'Recommended', value: 'recommended' },
  ];

  statusItems = [
    { viewValue: 'Not Started', value: 'not_started' },
    { viewValue: 'In Progress', value: 'in_progress' },
    { viewValue: 'Completed', value: 'completed' },
    { viewValue: 'Overdue', value: 'overdue' },
  ];

  frequencyItems = [
    { viewValue: 'None', value: '' },
    { viewValue: 'Ongoing', value: 'ongoing' },
    { viewValue: 'Recurring', value: 'recurring' },
    { viewValue: 'Monthly', value: 'monthly' },
    { viewValue: 'Quarterly', value: 'quarterly' },
    { viewValue: 'Annually', value: 'annually' },
  ];

  constructor() {
    this.form = this.fb.group({
      name: [''],
      bucket: [''],
      what_to_do: [''],
      how_to_do_it: [''],
      frequency: [''],
      next_due_at: [''],
      status: ['not_started', Validators.required],
      jurisdiction: [''],
      notes: [''],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    this.rightSidePanelService.setDialogPageTitle('Compliance Item');
    await this.loadItem();

    this.form.valueChanges.pipe(
      filter(() => this.initialLoadComplete),
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.performAutoSave();
    });
  }

  async loadItem() {
    try {
      // Desktop passes params via storage (right-side panel); mobile passes
      // them through the nav controller, so fall back to the nav param.
      const rawItemId = this.dialogParams?.complianceItemId
        ?? this.navCtrl.getParam('complianceItemId');
      if (!rawItemId) {
        this.errorMessage = 'No compliance item ID provided';
        this.loaded = true;
        this.cdr.markForCheck();
        return;
      }
      this.item = await firstValueFrom(this.complianceApi.getItem(Number(rawItemId)));
      this.form.patchValue({
        name: this.item.name || '',
        bucket: this.item.bucket || '',
        what_to_do: this.item.what_to_do || '',
        how_to_do_it: this.item.how_to_do_it || '',
        frequency: this.item.frequency || '',
        next_due_at: this.item.next_due_at || '',
        status: this.item.status || 'not_started',
        jurisdiction: this.item.jurisdiction || '',
        notes: this.item.notes || '',
      });
      // Use a stable title so long item descriptions don't overlap the dialog close button.
      this.rightSidePanelService.setDialogPageTitle('Compliance Item');
      this.loaded = true;
      this.cdr.markForCheck();
      setTimeout(() => this.initialLoadComplete = true);
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load compliance item';
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  private async performAutoSave() {
    if (this.isSaving) {
      this.pendingSave = true;
      return;
    }
    this.isSaving = true;
    this.rightSidePanelService.headerSpinnerVisible$.next(true);
    this.cdr.markForCheck();
    try {
      const formValue = this.form.value;
      await firstValueFrom(this.complianceApi.updateItem(this.item.id, {
        params: {
          name: formValue.name,
          bucket: formValue.bucket,
          what_to_do: formValue.what_to_do || null,
          how_to_do_it: formValue.how_to_do_it || null,
          frequency: formValue.frequency || null,
          next_due_at: formValue.next_due_at || null,
          status: formValue.status,
          jurisdiction: formValue.jurisdiction || null,
          notes: formValue.notes || null,
        },
      }));
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save';
    } finally {
      this.isSaving = false;
      this.rightSidePanelService.headerSpinnerVisible$.next(false);
      this.cdr.markForCheck();
      if (this.pendingSave) {
        this.pendingSave = false;
        this.performAutoSave();
      }
    }
  }

  async deleteItem() {
    const alert = await this.util.showConfirmAlert({
      message: 'Are you sure you want to delete this compliance item?',
    });
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') { return; }
    try {
      await firstValueFrom(this.complianceApi.deleteItem(this.item.id));
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateBack('compliance');
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to delete';
      this.cdr.markForCheck();
    }
  }

  getConfidenceColor(): string {
    switch (this.item?.confidence) {
      case 'high': return '#2E7D32';
      case 'medium': return '#F9A825';
      case 'low': return '#C62828';
      default: return '#999';
    }
  }

  getConfidenceLabel(): string {
    switch (this.item?.confidence) {
      case 'high': return 'Confidence: High';
      case 'medium': return 'Confidence: Medium';
      case 'low': return 'Confidence: Low';
      default: return '';
    }
  }

  formatTaxRate(): string {
    if (this.item?.tax_rate_cents === null || this.item?.tax_rate_cents === undefined) { return ''; }
    return `${(this.item.tax_rate_cents / 100).toFixed(2)  }%`;
  }
}
